import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
interface CoreService {
  image: string;
  alt: string;
  title: string;
  features: string;
  description: string;
  cta: string;
  href: string;
}
const coreServices: CoreService[] = [{
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/99ea7fbc-0001-4af3-b0eb-d47d3d9f1f5f.jpg',
  alt: 'A&P mechanics servicing a commercial jet engine inside a maintenance hangar',
  title: 'Aircraft Maintenance',
  features: 'Line Maintenance • AOG Support • Troubleshooting • Aircraft Recovery',
  description: 'Professional technical support when and where you need it. Our FAA-certified A&P mechanics ensure your aircraft stays airworthy and compliant.',
  cta: 'Explore Maintenance',
  href: '#maintenance'
}, {
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/c3ac06af-1bda-4ece-9298-62bc009727a3.jpg',
  alt: 'Detailing crew polishing the exterior fuselage of a private business jet',
  title: 'Aircraft Cleaning & Detailing',
  features: 'Interior Cleaning • Exterior Cleaning • Deep Cleaning • Turnaround Services',
  description: 'Professional aircraft care for commercial and private aviation. Airline and MRO approved cleaning services with attention to every detail.',
  cta: 'Explore Cleaning',
  href: '#cleaning'
}, {
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/4112de88-77a1-407d-97d5-11d948a840f4.jpg',
  alt: 'Jet engine secured in a shipping cradle on a flatbed truck at an airport apron',
  title: 'Aviation Transport & Logistics',
  features: 'Aircraft Parts • Engines • AOG Transportation • Specialized Aviation Logistics',
  description: 'Fast, dependable transportation solutions built around aviation. Nationwide coverage with 24/7 dispatch for time-critical deliveries.',
  cta: 'Explore Transport',
  href: '#services'
}];
export function CoreServices() {
  return <section id="core-services" className="py-20 bg-gradient-to-b from-[#0F172A] to-[#1A2A4A] relative border-b border-white/5" data-id="element-160" data-mp="2c80k4u9gk7mb">
      <div className="max-w-7xl mx-auto px-6 sm:px-10" data-id="element-161" data-mp="2m4vldkx48m4a">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }} className="text-center mb-14" data-id="element-162" data-mp="2m3apnrz2ex4u">
          <span className="text-gold text-sm font-bold tracking-widest uppercase mb-3 block" data-id="element-163" data-mp="2zv8qzgmjboqg">
            Our Core Services
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 uppercase" data-id="element-164" data-mp="34ta99zc4zk0v">
            More Than Just Transport
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-165" data-mp="1ai5ap9fwpxse">
            Comprehensive aviation logistics solutions designed for your
            operational needs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10" data-id="element-166" data-mp="17xz752v7g9cd">
          {coreServices.map((service, index) => <motion.article key={service.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.08,
          ease: [0.23, 1, 0.32, 1]
        }} className="group flex flex-col bg-navy/70 backdrop-blur-sm rounded-2xl overflow-hidden border border-gold/20 transition-[transform,border-color,box-shadow] duration-200 ease-out hover:-translate-y-2 hover:border-gold/70 hover:shadow-[0_18px_40px_-12px_rgba(201,168,76,0.28)]" data-id="element-167" data-mp="134wcereoytul" data-mpi={index}>
              <div className="relative h-[280px] w-full overflow-hidden shrink-0" data-id="element-168" data-mp="5ji7xzmucsre">
                <img src={service.image} alt={service.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-169" data-mp="j2s91f37969" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/10 to-transparent" aria-hidden="true" data-id="element-170" data-mp="1ffdn3lhntrfe" />
              </div>

              <div className="flex flex-col flex-1 p-7" data-id="element-171" data-mp="28419jke5amuj">
                <h3 className="text-2xl font-heading font-bold text-white uppercase tracking-wide leading-tight mb-3" data-id="element-172" data-mp="284khc8a2zuuu">
                  {service.title}
                </h3>

                <p className="text-gold text-[13px] font-semibold uppercase tracking-wide leading-relaxed mb-4" data-id="element-173" data-mp="1y4tn87pfnhva">
                  {service.features}
                </p>

                <p className="text-[15px] text-slate-300 leading-relaxed mb-7" data-id="element-174" data-mp="mqy64teywz1f">
                  {service.description}
                </p>

                <a href={service.href} className="mt-auto self-start inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-bold text-sm uppercase tracking-wider py-3 px-6 rounded-full transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy" data-id="element-175" data-mp="rqkk552mejba">
                  {service.cta}
                  <ArrowRight className="w-4 h-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-176" data-mp="2n72fhkd5x792" />
                </a>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}